export type Language = 'en' | 'yo' | 'ig' | 'ha';

export const LANGUAGES: { code: Language; label: string; native: string }[] = [
    { code: 'en', label: 'English', native: 'English' },
    { code: 'yo', label: 'Yoruba', native: 'Yorùbá' },
    { code: 'ig', label: 'Igbo', native: 'Igbo' },
    { code: 'ha', label: 'Hausa', native: 'Hausa' },
];

export const translations: Record<Language, Record<string, string>> = {
    // ─── ENGLISH ───
    en: {
        // Nav
        'nav.home': 'Home',
        'nav.services': 'Services',
        'nav.help': 'Help',
        'nav.signUp': 'Sign Up',
        'nav.login': 'Login',
        'nav.signIn': 'Sign In',
        'nav.getStarted': 'Get Started',

        // Hero
        'hero.socialProof': '33K+',
        'hero.socialProofText': 'Signed Up already',
        'hero.heading1': 'The Super App for ',
        'hero.heading2': 'Everything Legal.',
        'hero.subtitle':
            'Podus integrates AI legal assistance, smart contract management, lawyer matchmaking, and human rights support — all powered by one secure Podus Account.',
        'hero.ctaGetStarted': 'Get Started',
        'hero.ctaHowItWorks': 'See How It Works',

        // Features
        'features.badge': 'Best Legal Ecosystem',
        'features.headingGray': 'A Super Legal App Built to Simplify ',
        'features.headingDark': 'Justice, Business, and Everyday Legal Life',
        'features.headingMobileGray': 'The Legal Super App for ',
        'features.headingMobileDark': 'Life and Business',
        'features.subtitleDesktop': 'Podus is an AI-powered super app transforming how people and businesses access.',
        'features.subtitleMobile':
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        'features.0.title': 'Legal Support Shouldn\u2019t Be a Privilege',
        'features.0.description':
            'Legal help shouldn\u2019t depend on your income. Whether you need instant answers, free representation, or affordable consultations with qualified lawyers, Podus ensures you never face a legal problem alone.',
        'features.1.title': 'Smarter Protection for Your Business',
        'features.1.description':
            'A single unclear clause can cost your business months of revenue. Podus Contract helps you draft, review, and manage agreements with AI-powered analysis and expert review by a lawyer in the relevant field',
        'features.2.title': 'Hire with Confidence',
        'features.2.description':
            'Podus is an AI-powered super app. Podus Checker reduces hiring risk with structured background checks on employment, criminal records, social media, education, and guarantors.',

        // Services
        'services.heading': 'Smart Legal Solutions for Every Situation',
        'services.subtitle':
            'Explore the Podus ecosystem — a suite of AI-powered legal services designed to meet every need. From instant legal advice to contract management and access to lawyers, everything you need for justice and business is in one place.',
        'services.0.description':
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        'services.0.cta': 'Try Our Legal Assistant',
        'services.1.description':
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        'services.1.cta': 'Run a Background Check',
        'services.2.description':
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        'services.2.cta': 'Get Started For Free',
        'services.3.description':
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        'services.3.cta': 'Book an Appointment',
        'services.4.description':
            'Podus is an AI-powered super app transforming how people and businesses access legal solutions — from instant guidance to contract management and human rights protection.',
        'services.4.cta': 'Request Aid',

        // Single Sign In
        'singleSignIn.tagline': 'One login for all.',
        'singleSignIn.heading': 'Single Sign In',
        'singleSignIn.description':
            'Access all Podus services with one secure account. No need to remember multiple passwords — sign in once and unlock the full suite of AI-powered legal tools, from contracts to consultations.',
        'singleSignIn.cta': 'Get Started',

        // Podus Features
        'podusFeatures.heading1': 'Podus AI ',
        'podusFeatures.heading2': 'Features',
        'podusFeatures.0.title': 'One Account. Every Legal Solution.',
        'podusFeatures.0.description':
            'Access all Podus services with a single secure login. Podus brings together legal guidance, lawyer consultations, dispute resolution, contract tools, background checks, and legal aid under one unified account. No switching platforms. No repeated sign-ins. Just one seamless legal ecosystem built around your needs.',
        'podusFeatures.1.title': 'AI-Powered Legal Intelligence',
        'podusFeatures.1.description':
            "Instant clarity before you speak to a lawyer. With Podus AI, users receive real-time legal guidance, document drafting support, and scenario analysis. Whether you're facing a dispute, reviewing a contract, or trying to understand your rights, Podus AI simplifies complex legal information into clear, actionable insights.",
        'podusFeatures.2.title': 'Connect With Real Lawyers — On Demand',
        'podusFeatures.2.description':
            'From AI advice to human expertise in minutes. Book virtual appointments, request contract reviews, or get matched with volunteer legal aid lawyers directly within the app. Podus bridges the gap between digital intelligence and professional legal support — securely and efficiently.',
        'podusFeatures.3.title': 'Smart Contract & Obligation Tracking',
        'podusFeatures.3.description':
            'Understand what you signed — and never miss a deadline. Podus Contract analyzes agreements, highlights risks, explains obligations, and syncs important dates to your calendar. From freelancers to SMEs, users gain confidence in every document they sign and every commitment they make.',
        'podusFeatures.4.title': 'Court-Connected Dispute Resolution',
        'podusFeatures.4.description':
            'File, track, and resolve disputes digitally. Through Podus Multidoor, users can submit cases directly to Multi-Door Courts for mediation, arbitration, or reconciliation. The platform simplifies case intake, handles payments, and keeps users updated — reducing court congestion while increasing access.',
        'podusFeatures.5.title': 'Secure & Trusted Legal Infrastructure',
        'podusFeatures.5.description':
            'Built for justice. Designed for protection. Podus operates with strong data protection standards, encrypted communication, role-based access control, and full audit trails. From sensitive case filings to background checks, every interaction is protected with privacy and accountability at its core.',

        // Glimpse
        'glimpse.heading': 'A Glimpse Of A Seamless Legal Support, Powered by Podus',
        'glimpse.subtitle':
            'Podus brings together all your legal needs in one connected ecosystem. With a single account, you can access multiple services — from AI-powered legal support to lawyer connections and contract management — all designed to make justice faster, smarter, and more accessible.',
        'glimpse.stats': '33k',
        'glimpse.statsLabel': 'Users onboarded already',
        'glimpse.howItWorks': 'How it works',

        // Regions
        'regions.heading': 'Supporting Users Across Nigeria, Ghana, and the United Kingdom',
        'regions.description':
            "Podus is growing across multiple regions, ensuring people and businesses can access reliable legal support wherever they are. Our services adapt to each country's legal environment\u2014offering free, paid, or restricted features based on local regulations and community needs.",
        'regions.tagline': 'Your Legal Companion Across Regions.',

        // Audience
        'audience.heading': 'Who Can Use Podus',
        'audience.subtitle':
            'Podus is designed for everyone — from individuals seeking justice to businesses managing contracts and lawyers looking to streamline their work. Whether you need quick legal guidance, affordable representation, or secure document management, Podus gives you the tools to take control of your legal journey.',
        'audience.0.title': 'Individuals & Citizens',
        'audience.0.description':
            'Access clear legal guidance, draft documents, resolve disputes, and connect with lawyers. Podus helps individuals understand their rights and take action without navigating complicated legal systems alone.',
        'audience.1.title': 'Businesses & Entrepreneurs',
        'audience.1.description':
            'Draft and analyze contracts, track obligations, and conduct employee background checks. Podus helps businesses reduce legal risk, make informed decisions, and manage agreements with greater clarity and confidence.',
        'audience.2.title': 'Lawyers & Legal Students & Professionals',
        'audience.2.description':
            'Offer consultations, review contracts, support dispute resolution, and participate in legal aid. Podus connects legal professionals with users while creating opportunities to build experience and expand professional impact.',
        'audience.3.title': 'NGOs & Human Rights Organizations',
        'audience.3.description':
            'Support communities, document rights violations, and connect affected individuals to legal support. Podus provides digital tools that help NGOs strengthen advocacy, manage cases, and scale justice initiatives.',
        'audience.stat': '70%',
        'audience.statLabel': 'Time Saved On\nRoutine Tasks',
        'audience.cta': 'Get Started',

        // Testimonials
        'testimonials.heading': 'Testimonies',
        'testimonials.0.quote':
            'As a small business owner, managing contracts used to be stressful. With Podus Contract, I can review and draft agreements quickly and confidently. It\u2019s like having a legal team on demand.',
        'testimonials.0.name': 'Michael Kaizer',
        'testimonials.0.role': 'General Manager, Research & Insights',
        'testimonials.1.quote':
            'Podus AI completely changed how I handle legal matters. The instant advice feature saved me from a costly landlord dispute. I couldn\u2019t believe how fast and accurate it was.',
        'testimonials.1.name': 'Amara Osei',
        'testimonials.1.role': 'Freelance Consultant',
        'testimonials.2.quote':
            'As a law student, having access to AI-powered research tools through Podus has been a game-changer. It helps me prepare cases faster and understand precedents better.',
        'testimonials.2.name': 'David Mensah',
        'testimonials.2.role': 'Law Student, University of Ghana',
        'testimonials.3.quote':
            'Our NGO uses Podus to document human rights cases across three countries. The platform has streamlined our workflow and helped us serve more communities effectively.',
        'testimonials.3.name': 'Fatima Bello',
        'testimonials.3.role': 'Program Director, Justice First NGO',
        'testimonials.4.quote':
            'With Podus Checker, running background checks on potential business partners is seamless. It gives us the confidence to make informed decisions every time.',
        'testimonials.4.name': 'Kwame Asante',
        'testimonials.4.role': 'CEO, TechBridge Solutions',

        // FAQ
        'faq.heading': 'Frequently Asked Questions',
        'faq.subtitle1': "Need help? We've ",
        'faq.subtitle2': 'got the answers',
        'faq.0.question': 'How do I get started?',
        'faq.0.answer':
            'Simply sign up for an account, and you can start chatting with our AI assistant. You can ask legal questions, create cases, and access various legal resources.',
        'faq.1.question': 'Does Podus AI replace Lawyers?',
        'faq.1.answer':
            'Podus AI does not replace the position of lawyers in solving legal cases. It rather acts as a first aid for legal problems',
        'faq.2.question': 'Is PodusAI free?',
        'faq.2.answer':
            'Yes. As of Q4 2025, PodusAI is completely free. Our mission is to reduce legal ignorance and empower Nigerians with accessible legal support while helping lawyers work more efficiently.',
        'faq.3.question': 'Is PodusAI only for lawyers?',
        'faq.3.answer':
            'No. PodusAI serves both lawyers and everyday Nigerians. Whether you need to identify government agencies, understand your rights, or get initial legal guidance, PodusAI is built for active citizenship.',
        'faq.4.question': 'As a non-lawyer, what can PodusAI help with?',
        'faq.4.answer':
            'PodusAI helps you report human rights violations and connect with pro bono lawyers; access Police PRO numbers for all 36 states in cases of brutality; Draft petitions, FOI requests, and complaint letters; Get legal guidance in plain terms.',
        'faq.5.question': 'Can PodusAI connect me with lawyers?',
        'faq.5.answer':
            "Yes. If your case involves human rights violations or gender-based abuse, we'll assess it and assign you a pro bono lawyer where applicable. Terms and conditions apply.",
    },

    // ─── YORUBA ───
    yo: {
        'nav.home': 'Ilé',
        'nav.services': 'Àwọn Iṣẹ́',
        'nav.help': 'Ìrànlọ́wọ́',
        'nav.signUp': 'Forúkọsílẹ̀',
        'nav.login': 'Wọlé',
        'nav.signIn': 'Wọlé',
        'nav.getStarted': 'Bẹ̀rẹ̀',

        'hero.socialProof': '31K+',
        'hero.socialProofText': 'Ti forúkọsílẹ̀ tẹ́lẹ̀',
        'hero.heading1': 'Àpò Àgbékalẹ̀ fún ',
        'hero.heading2': 'Gbogbo Ọ̀rọ̀ Òfin.',
        'hero.subtitle':
            'Podus so ìrànlọ́wọ́ òfin AI, ìṣàkóso àdéhùn, ìsopọ̀ agbẹjọ́rò, àti àtìlẹ́yìn ẹ̀tọ́ ọmọnìyàn pọ̀ — gbogbo rẹ̀ ní ìpìlẹ̀ àkáǹtì Podus kan.',
        'hero.ctaGetStarted': 'Bẹ̀rẹ̀',
        'hero.ctaHowItWorks': 'Wo Bí Ó Ṣe Ń Ṣiṣẹ́',

        'features.badge': 'Ètò Òfin Tó Dára Jùlọ',
        'features.headingGray': 'Àpò Òfin Tó Rọrùn Láti Mú ',
        'features.headingDark': 'Ìdájọ́, Iṣòwò, àti Ìgbésí Ayé Òfin Lójoojúmọ́',
        'features.headingMobileGray': 'Àpò Òfin Àgbékalẹ̀ fún ',
        'features.headingMobileDark': 'Ìgbésí Ayé àti Iṣòwò',
        'features.subtitleDesktop':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI láti yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí.',
        'features.subtitleMobile':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI láti yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí àwọn ojútùú òfin — láti ìtọ́sọ́nà lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìdáàbòbò ẹ̀tọ́ ọmọnìyàn.',
        'features.0.title': 'Àtìlẹ́yìn Òfin Kò Gbọ́dọ̀ Jẹ́ Àǹfààní',
        'features.0.description':
            'Ìrànlọ́wọ́ òfin kò gbọ́dọ̀ dá lórí owó rẹ. Bóyá o nílò ìdáhùn lẹ́sẹ̀kẹsẹ̀, aṣojú ọ̀fẹ́, tàbí ìjíròrò tó wọ́n pọ̀ pẹ̀lú àwọn agbẹjọ́rò tó yẹ, Podus rí i dájú pé o kò ní kojú ìṣòro òfin nìkan.',
        'features.1.title': 'Àbò Tó Gbọ́n Jùlọ Fún Iṣòwò Rẹ',
        'features.1.description':
            'Gbólóhùn kan tí kò ṣe kedere lè ná iṣòwò rẹ oṣù mẹ́ta owó wọlé. Podus Contract ṣe ìrànlọ́wọ́ fún ọ láti kọ, ṣàyẹ̀wò, àti ṣàkóso àwọn àdéhùn pẹ̀lú ìtúpalẹ̀ AI àti àyẹ̀wò ọ̀gá nínú agbẹjọ́rò tó bá àyè mu.',
        'features.2.title': 'Gbé Ènìyàn Gba Pẹ̀lú Ìgbẹ́kẹ̀lé',
        'features.2.description':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI. Podus Checker dín ewu gbígba ènìyàn kù pẹ̀lú àyẹ̀wò ìpìlẹ̀ tó ní ètò lórí iṣẹ́, ìwà ọ̀daràn, àwùjọ àyélujára, ẹ̀kọ́, àti àwọn aṣojú.',

        'services.heading': 'Àwọn Ojútùú Òfin Tó Gbọ́n Fún Gbogbo Ipò',
        'services.subtitle':
            'Ṣàwárí ètò Podus — oríṣiríṣi iṣẹ́ òfin tó ń lo AI tó dá sílẹ̀ láti pàdé gbogbo àìní. Láti ìmọ̀ràn òfin lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìsopọ̀ agbẹjọ́rò, ohun gbogbo tí o nílò fún ìdájọ́ àti iṣòwò wà ní ibi kan.',
        'services.0.description':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI tó ń yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí àwọn ojútùú òfin — láti ìtọ́sọ́nà lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìdáàbòbò ẹ̀tọ́ ọmọnìyàn.',
        'services.0.cta': 'Gbìyànjú Olùrànlọ́wọ́ Òfin Wa',
        'services.1.description':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI tó ń yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí àwọn ojútùú òfin — láti ìtọ́sọ́nà lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìdáàbòbò ẹ̀tọ́ ọmọnìyàn.',
        'services.1.cta': 'Ṣe Àyẹ̀wò Ìpìlẹ̀',
        'services.2.description':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI tó ń yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí àwọn ojútùú òfin — láti ìtọ́sọ́nà lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìdáàbòbò ẹ̀tọ́ ọmọnìyàn.',
        'services.2.cta': 'Bẹ̀rẹ̀ Lọ́fẹ̀ẹ́',
        'services.3.description':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI tó ń yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí àwọn ojútùú òfin — láti ìtọ́sọ́nà lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìdáàbòbò ẹ̀tọ́ ọmọnìyàn.',
        'services.3.cta': 'Ṣètò Ìpàdé',
        'services.4.description':
            'Podus jẹ́ àpò àgbékalẹ̀ tó ń lo AI tó ń yí padà bí àwọn ènìyàn àti iṣòwò ṣe ń wọlé sí àwọn ojútùú òfin — láti ìtọ́sọ́nà lẹ́sẹ̀kẹsẹ̀ sí ìṣàkóso àdéhùn àti ìdáàbòbò ẹ̀tọ́ ọmọnìyàn.',
        'services.4.cta': 'Béèrè fún Ìrànlọ́wọ́',

        'singleSignIn.tagline': 'Ìwọlé kan fún gbogbo rẹ̀.',
        'singleSignIn.heading': 'Ìwọlé Kan Ṣoṣo',
        'singleSignIn.description':
            'Wọlé sí gbogbo iṣẹ́ Podus pẹ̀lú àkáǹtì kan tó ní ààbò. Kò sí àìní láti rántí ọ̀pọ̀lọpọ̀ ọ̀rọ̀ aṣínà — wọlé lẹ́ẹ̀kan kí o sì ṣí gbogbo ètò àwọn irinṣẹ́ òfin tó ń lo AI, láti àdéhùn sí ìjíròrò.',
        'singleSignIn.cta': 'Bẹ̀rẹ̀',

        'podusFeatures.heading1': 'Podus AI ',
        'podusFeatures.heading2': 'Àwọn Ẹ̀yà',
        'podusFeatures.0.title': 'Àkáǹtì Kan. Gbogbo Ojútùú Òfin.',
        'podusFeatures.0.description':
            'Wọlé sí gbogbo iṣẹ́ Podus pẹ̀lú ìwọlé kan tó ní ààbò. Podus kó ìtọ́sọ́nà òfin, ìjíròrò agbẹjọ́rò, ìyanjú àríyànjiyàn, àwọn irinṣẹ́ àdéhùn, àyẹ̀wò ìpìlẹ̀, àti ìrànlọ́wọ́ òfin jọ lábẹ́ àkáǹtì kan. Kò sí yíyípadà ètò. Kò sí ìwọlé léèmejì. Ètò òfin kan tó rọrùn tó dá lé lórí àìní rẹ.',
        'podusFeatures.1.title': 'Ìmọ̀ Òfin Tó Ń Lo AI',
        'podusFeatures.1.description':
            'Ìmọ̀ lẹ́sẹ̀kẹsẹ̀ kí o tó bá agbẹjọ́rò sọ̀rọ̀. Pẹ̀lú Podus AI, àwọn olùlò ń gba ìtọ́sọ́nà òfin lẹ́sẹ̀kẹsẹ̀, àtìlẹ́yìn kíkọ àkọsílẹ̀, àti ìtúpalẹ̀ ipò. Podus AI mú àlàyé tó ṣòro rọrùn sí àwọn ìmọ̀ràn tó ṣe kedere.',
        'podusFeatures.2.title': 'Sopọ̀ Pẹ̀lú Agbẹjọ́rò Gidi — Lórí Ìbéèrè',
        'podusFeatures.2.description':
            'Láti ìmọ̀ràn AI sí ìmọ̀ ènìyàn ní ìṣẹ́jú. Ṣètò ìpàdé fojú ìwòran, béèrè fún àyẹ̀wò àdéhùn, tàbí bá agbẹjọ́rò olùyọ̀ǹda pàdé tààrà nínú àpò náà.',
        'podusFeatures.3.title': 'Àdéhùn Ọlọ́gbọ́n & Ìtọpinpin Ojúṣe',
        'podusFeatures.3.description':
            'Mọ ohun tí o fọwọ́sí — má sì padà gbàgbé ọjọ́ ìparí. Podus Contract ṣe ìtúpalẹ̀ àwọn àdéhùn, ṣàfihàn ewu, ṣàlàyé ojúṣe, ó sì so àwọn ọjọ́ pàtàkì pọ̀ mọ́ kàlẹ́ndà rẹ.',
        'podusFeatures.4.title': 'Ìyanjú Àríyànjiyàn Tó Sopọ̀ Mọ́ Ilé Ẹjọ́',
        'podusFeatures.4.description':
            'Fi ẹjọ́ sílẹ̀, tọpinpin, kí o sì yanjú àríyànjiyàn lórí ayélujára. Nípasẹ̀ Podus Multidoor, àwọn olùlò lè fi ẹjọ́ tààrà sí Ilé Ẹjọ́ Ọ̀nà Púpọ̀ fún ìlaja, àdájọ́, tàbí ìlàjà.',
        'podusFeatures.5.title': 'Ètò Àbẹ́lé Òfin Tó Ní Ààbò & Ìgbẹ́kẹ̀lé',
        'podusFeatures.5.description':
            'Kọ́ fún ìdájọ́. Dá fún ìdáàbòbò. Podus ń ṣiṣẹ́ pẹ̀lú ìlànà ìdáàbòbò dátà tó lágbára, ìbánisọ̀rọ̀ tí a fi pamọ́, àkóso ìráyè tó dá lé lórí ipò, àti ọ̀nà àyẹ̀wò kíkún.',

        'glimpse.heading': 'Wòye Àtìlẹ́yìn Òfin Tó Rọrùn, Tí Podus Ṣe Agbára Rẹ̀',
        'glimpse.subtitle':
            'Podus kó gbogbo àìní òfin rẹ jọ nínú ètò kan tó sopọ̀. Pẹ̀lú àkáǹtì kan, o lè wọlé sí ọ̀pọ̀lọpọ̀ iṣẹ́ — láti àtìlẹ́yìn òfin tó ń lo AI sí ìsopọ̀ agbẹjọ́rò àti ìṣàkóso àdéhùn.',
        'glimpse.stats': '33k',
        'glimpse.statsLabel': 'Àwọn olùlò tí wọ́n ti wọlé tẹ́lẹ̀',
        'glimpse.howItWorks': 'Bí ó ṣe ń ṣiṣẹ́',

        'regions.heading': 'Àtìlẹ́yìn Àwọn Olùlò Kárí Nàìjíríà, Ghana, àti United Kingdom',
        'regions.description':
            'Podus ń dàgbà kárí àwọn àgbègbè púpọ̀, tó ń rí i dájú pé àwọn ènìyàn àti iṣòwò lè wọlé sí àtìlẹ́yìn òfin tó ṣe é gbẹ́kẹ̀lé níbikíbi tí wọ́n bá wà.',
        'regions.tagline': 'Alábàáṣiṣẹ́pọ̀ Òfin Rẹ Kárí Àwọn Àgbègbè.',

        'audience.heading': 'Ta Ni Ó Lè Lo Podus',
        'audience.subtitle':
            'Podus ni a ṣe fún gbogbo ènìyàn — láti àwọn ẹnìkọ̀ọ̀kan tó ń wá ìdájọ́ sí àwọn iṣòwò tó ń ṣàkóso àdéhùn àti àwọn agbẹjọ́rò tó ń wá ọ̀nà láti mú iṣẹ́ wọn rọrùn.',
        'audience.0.title': 'Àwọn Ẹnìkọ̀ọ̀kan & Aráàlú',
        'audience.0.description':
            'Wọlé sí ìtọ́sọ́nà òfin tó kedere, kọ àwọn àkọsílẹ̀, yanjú àríyànjiyàn, kí o sì sopọ̀ pẹ̀lú àwọn agbẹjọ́rò.',
        'audience.1.title': 'Àwọn Iṣòwò & Olùdásílẹ̀',
        'audience.1.description': 'Kọ kí o sì ṣe ìtúpalẹ̀ àdéhùn, tọpinpin ojúṣe, kí o sì ṣe àyẹ̀wò ìpìlẹ̀ àwọn ọ̀gá iṣẹ́.',
        'audience.2.title': 'Àwọn Agbẹjọ́rò & Akẹ́kọ̀ọ́ Òfin & Àwọn Ọjọ́gbọ́n',
        'audience.2.description':
            'Pèsè ìjíròrò, ṣàyẹ̀wò àdéhùn, ṣe àtìlẹ́yìn ìyanjú àríyànjiyàn, kí o sì kópa nínú ìrànlọ́wọ́ òfin.',
        'audience.3.title': 'Àwọn Àjọ Aláìjẹ́-ère & Àwọn Àjọ Ẹ̀tọ́ Ọmọnìyàn',
        'audience.3.description':
            'Ṣe àtìlẹ́yìn àwùjọ, ṣe àkọsílẹ̀ ìrúfin ẹ̀tọ́, kí o sì so àwọn ènìyàn tó kan pọ̀ mọ́ àtìlẹ́yìn òfin.',
        'audience.stat': '70%',
        'audience.statLabel': 'Àkókò Tí A Fipamọ́ Lórí\nÀwọn Iṣẹ́ Ìgbàgbogbo',
        'audience.cta': 'Bẹ̀rẹ̀',

        'testimonials.heading': 'Àwọn Ẹ̀rí',

        'faq.heading': 'Àwọn Ìbéèrè Tí A Má Ń Béèrè Sí',
        'faq.subtitle1': 'Ṣé o nílò ìrànlọ́wọ́? A ti ',
        'faq.subtitle2': 'ní àwọn ìdáhùn',
        'faq.0.question': 'Báwo ni mo ṣe lè bẹ̀rẹ̀?',
        'faq.0.answer':
            'Forúkọsílẹ̀ fún àkáǹtì kan, o sì lè bẹ̀rẹ̀ sí bá olùrànlọ́wọ́ AI wa sọ̀rọ̀. O lè béèrè àwọn ìbéèrè òfin, ṣẹ̀dá àwọn ẹjọ́, kí o sì wọlé sí oríṣiríṣi ohun èlò òfin.',
        'faq.1.question': 'Ṣé Podus AI rọ́pò Àwọn Agbẹjọ́rò?',
        'faq.1.answer':
            'Podus AI kò rọ́pò ipò àwọn agbẹjọ́rò nínú yíyanjú àwọn ẹjọ́ òfin. Ó kàn ṣiṣẹ́ gẹ́gẹ́ bí ìrànlọ́wọ́ àkọ́kọ́ fún àwọn ìṣòro òfin.',
        'faq.2.question': 'Ṣé PodusAI jẹ́ ọ̀fẹ́?',
        'faq.2.answer':
            'Bẹ́ẹ̀ni. PodusAI jẹ́ ọ̀fẹ́ pátápátá. Ète wa ni láti dín àìmọ̀ òfin kù àti láti fi agbára fún àwọn ará Nàìjíríà pẹ̀lú àtìlẹ́yìn òfin tó rọrùn.',
        'faq.3.question': 'Ṣé PodusAI jẹ́ fún àwọn agbẹjọ́rò nìkan?',
        'faq.3.answer':
            'Rárá. PodusAI ṣiṣẹ́ fún àwọn agbẹjọ́rò àti àwọn ará Nàìjíríà lójoojúmọ́. Bóyá o nílò láti dá àwọn àjọ ìjọba mọ̀, mọ ẹ̀tọ́ rẹ, tàbí gba ìtọ́sọ́nà òfin àkọ́kọ́, PodusAI ni a kọ́ fún aráàlú tó ṣe é gbẹ́kẹ̀lé.',
        'faq.4.question': 'Gẹ́gẹ́ bí ẹni tí kìí ṣe agbẹjọ́rò, kí ni PodusAI lè ṣe ìrànlọ́wọ́ pẹ̀lú?',
        'faq.4.answer':
            'PodusAI ṣe ìrànlọ́wọ́ fún ọ láti fi ẹ̀sùn ìrúfin ẹ̀tọ́ ọmọnìyàn àti láti sopọ̀ pẹ̀lú àwọn agbẹjọ́rò olùyọ̀ǹda; wọlé sí àwọn nọ́mbà PRO Ọlọ́pàá fún gbogbo ìpínlẹ̀ 36.',
        'faq.5.question': 'Ṣé PodusAI lè so mí pọ̀ mọ́ àwọn agbẹjọ́rò?',
        'faq.5.answer':
            'Bẹ́ẹ̀ni. Tí ẹjọ́ rẹ bá kan ìrúfin ẹ̀tọ́ ọmọnìyàn tàbí ìlòkulò tó dá lé lórí akọ-abo, a ó ṣe àyẹ̀wò rẹ̀ kí a sì fi agbẹjọ́rò olùyọ̀ǹda lélẹ̀ tó bá yẹ.',
    },

    // ─── IGBO ───
    ig: {
        'nav.home': 'Ụlọ',
        'nav.services': 'Ọrụ Ndị',
        'nav.help': 'Enyemaka',
        'nav.signUp': 'Debanye Aha',
        'nav.login': 'Banye',
        'nav.signIn': 'Banye',
        'nav.getStarted': 'Malite',

        'hero.socialProof': '31K+',
        'hero.socialProofText': 'Edebanyela aha',
        'hero.heading1': 'Ngwa Kachasị Maka ',
        'hero.heading2': 'Ihe Niile Gbasara Iwu.',
        'hero.subtitle':
            'Podus jikọtara enyemaka iwu AI, njikwa nkwekọrịta, ijikọ ndị ọka iwu, na nkwado ikike mmadụ — nke niile sitere na otu akaụntụ Podus echekwara.',
        'hero.ctaGetStarted': 'Malite',
        'hero.ctaHowItWorks': 'Lee Ka Ọ Si Arụ Ọrụ',

        'features.badge': 'Usoro Iwu Kachasị Mma',
        'features.headingGray': 'Ngwa Iwu Kachasị Maka Ime Ka Ọ Dị Mfe ',
        'features.headingDark': 'Ikpe Ziri Ezi, Azụmaahịa, na Ndụ Iwu Ụbọchị Niile',
        'features.headingMobileGray': 'Ngwa Iwu Kachasị Maka ',
        'features.headingMobileDark': 'Ndụ na Azụmaahịa',
        'features.subtitleDesktop': 'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta.',
        'features.subtitleMobile':
            'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta ihe ngwọta iwu — site na nduzi ozugbo ruo njikwa nkwekọrịta na nchekwa ikike mmadụ.',
        'features.0.title': 'Nkwado Iwu Ekwesịghị Ịbụ Uru Pụrụ Iche',
        'features.0.description':
            "Enyemaka iwu ekwesịghị ịdabere na ego gị. Ma ọ bụ ịchọrọ azịza ozugbo, onye nnọchi anya n'efu, ma ọ bụ mkparịta ụka ndị ọka iwu ruru eru, Podus na-ahụ na ị gaghị echere nsogbu iwu naanị gị.",
        'features.1.title': 'Nchekwa Amamihe Maka Azụmaahịa Gị',
        'features.1.description':
            'Otu nkebi okwu na-adịghị mfe nwere ike ịkpa azụmaahịa gị ego ọnwa atọ. Podus Contract na-enyere gị aka ide, nyochaa, ma jikwaa nkwekọrịta site na nyocha AI na nyocha ọkachamara.',
        'features.2.title': "Gote Ndị Ọrụ N'obi Iru Ala",
        'features.2.description':
            'Podus bụ ngwa AI. Podus Checker na-ebelata ihe ize ndụ ịgote ndị ọrụ site na nyocha ndabere nke ọrụ, ndekọ mpụ, social media, agụmakwụkwọ, na ndị nkwado.',

        'services.heading': 'Ihe Ngwọta Iwu Nwere Amamihe Maka Ọnọdụ Ọ Bụla',
        'services.subtitle':
            "Nyochaa usoro Podus — ụdị ọrụ iwu AI emebere iji gboo mkpa niile. Site na ndụmọdụ iwu ozugbo ruo njikwa nkwekọrịta na ịnweta ndị ọka iwu, ihe niile ịchọrọ maka ikpe ziri ezi na azụmaahịa dị n'otu ebe.",
        'services.0.description':
            'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta ihe ngwọta iwu — site na nduzi ozugbo ruo njikwa nkwekọrịta na nchekwa ikike mmadụ.',
        'services.0.cta': 'Nwale Onye Enyemaka Iwu Anyị',
        'services.1.description':
            'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta ihe ngwọta iwu — site na nduzi ozugbo ruo njikwa nkwekọrịta na nchekwa ikike mmadụ.',
        'services.1.cta': 'Mee Nyocha Ndabere',
        'services.2.description':
            'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta ihe ngwọta iwu — site na nduzi ozugbo ruo njikwa nkwekọrịta na nchekwa ikike mmadụ.',
        'services.2.cta': "Malite N'efu",
        'services.3.description':
            'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta ihe ngwọta iwu — site na nduzi ozugbo ruo njikwa nkwekọrịta na nchekwa ikike mmadụ.',
        'services.3.cta': 'Debe Nzukọ',
        'services.4.description':
            'Podus bụ ngwa AI na-agbanwe otu ndị mmadụ na azụmaahịa si enweta ihe ngwọta iwu — site na nduzi ozugbo ruo njikwa nkwekọrịta na nchekwa ikike mmadụ.',
        'services.4.cta': 'Rịọ Enyemaka',

        'singleSignIn.tagline': 'Otu mbanye maka ihe niile.',
        'singleSignIn.heading': 'Otu Mbanye',
        'singleSignIn.description':
            "Nweta ohere n'ọrụ Podus niile site na otu akaụntụ echekwara. Ọ dịghị mkpa icheta ọtụtụ okwuntụghe — banye otu ugboro wee meghe ngwa iwu AI niile.",
        'singleSignIn.cta': 'Malite',

        'podusFeatures.heading1': 'Podus AI ',
        'podusFeatures.heading2': 'Atụmatụ',
        'podusFeatures.0.title': 'Otu Akaụntụ. Ihe Ngwọta Iwu Ọ Bụla.',
        'podusFeatures.0.description':
            "Nweta ohere n'ọrụ Podus niile site na otu mbanye echekwara. Podus na-ejikọta nduzi iwu, mkparịta ụka ndị ọka iwu, mkpebi esemokwu, ngwa nkwekọrịta, nyocha ndabere, na enyemaka iwu n'okpuru otu akaụntụ.",
        'podusFeatures.1.title': 'Ọgụgụ Isi Iwu Nke AI',
        'podusFeatures.1.description':
            'Nghọta ozugbo tupu ị gwa ọka iwu okwu. Site na Podus AI, ndị ọrụ na-enweta nduzi iwu ozugbo, nkwado ide akwụkwọ, na nyocha ọnọdụ.',
        'podusFeatures.2.title': "Jikọọ Na Ndị Ọka Iwu Ezigbo — N'arịrịọ",
        'podusFeatures.2.description':
            "Site na ndụmọdụ AI ruo amamihe mmadụ na nkeji ole na ole. Debe nzukọ vidiyo, rịọ nyocha nkwekọrịta, ma ọ bụ jikọọ na ndị ọka iwu n'efu n'ime ngwa ahụ.",
        'podusFeatures.3.title': 'Nkwekọrịta Amamihe & Nsochi Ọrụ',
        'podusFeatures.3.description':
            'Ghọta ihe ị binyere aka — hapụkwa ịtufu oge njedebe. Podus Contract na-enyocha nkwekọrịta, na-egosipụta ihe ize ndụ, na-akọwa ọrụ, wee jikọọ ụbọchị dị mkpa na kalenda gị.',
        'podusFeatures.4.title': "Mkpebi Esemokwu Ejikọtara N'Ụlọ Ikpe",
        'podusFeatures.4.description':
            'Debee, sochie, ma dozie esemokwu na dijitalụ. Site na Podus Multidoor, ndị ọrụ nwere ike itinye ikpe ozugbo na Ụlọ Ikpe Multi-Door maka mkpakọrịta, mkpebi, ma ọ bụ nkwurịta okwu.',
        'podusFeatures.5.title': 'Akụrụngwa Iwu Echekwara & Ntụkwasị Obi',
        'podusFeatures.5.description':
            'Arụrụ maka ikpe ziri ezi. Emebere maka nchekwa. Podus na-arụ ọrụ na ụkpụrụ nchekwa data siri ike, nkwurịta okwu ezoro ezo, njikwa ohere dabere na ọrụ, na ụzọ nyocha zuru oke.',

        'glimpse.heading': "Anya N'elu Nkwado Iwu Enweghị Nsogbu, Nke Podus Kwadoro",
        'glimpse.subtitle':
            "Podus na-ejikọta mkpa iwu gị niile n'otu usoro ejikọtara. Site na otu akaụntụ, ị nwere ike ịnweta ọtụtụ ọrụ — site na nkwado iwu AI ruo njikọ ndị ọka iwu na njikwa nkwekọrịta.",
        'glimpse.stats': '33k',
        'glimpse.statsLabel': 'Ndị ọrụ edebanye aha',
        'glimpse.howItWorks': 'Ka ọ si arụ ọrụ',

        'regions.heading': "Na-akwado Ndị Ọrụ N'ofe Nigeria, Ghana, na United Kingdom",
        'regions.description':
            "Podus na-etolite n'ofe mpaghara ọtụtụ, na-ahụ na ndị mmadụ na azụmaahịa nwere ike ịnweta nkwado iwu ntụkwasị obi ebe ọ bụla ha nọ.",
        'regions.tagline': "Onye Mmekọ Iwu Gị N'ofe Mpaghara.",

        'audience.heading': 'Onye Nwere Ike Iji Podus',
        'audience.subtitle':
            'Emebere Podus maka onye ọ bụla — site na ndị mmadụ na-achọ ikpe ziri ezi ruo azụmaahịa na-ejikwa nkwekọrịta na ndị ọka iwu na-achọ ime ka ọrụ ha dị mfe.',
        'audience.0.title': 'Ndị Mmadụ & Ndị Obodo',
        'audience.0.description': 'Nweta nduzi iwu doro anya, dee akwụkwọ, dozie esemokwu, jikọọkwa na ndị ọka iwu.',
        'audience.1.title': 'Azụmaahịa & Ndị Ọchịchị Azụmaahịa',
        'audience.1.description': 'Dee ma nyochaa nkwekọrịta, sochie ọrụ, meekwa nyocha ndabere ndị ọrụ.',
        'audience.2.title': 'Ndị Ọka Iwu & Ụmụ Akwụkwọ Iwu & Ndị Ọkachamara',
        'audience.2.description':
            'Nye mkparịta ụka, nyochaa nkwekọrịta, kwado mkpebi esemokwu, sonyekwa na enyemaka iwu.',
        'audience.3.title': 'Ndị NGO & Ndị Nzukọ Ikike Mmadụ',
        'audience.3.description': 'Kwado obodo, dee ndekọ mmebi ikike, jikọọkwa ndị metụtara na nkwado iwu.',
        'audience.stat': '70%',
        'audience.statLabel': 'Oge Echekwara Na\nỌrụ Ụbọchị Niile',
        'audience.cta': 'Malite',

        'testimonials.heading': 'Àmà',

        'faq.heading': 'Ajụjụ A Na-Ajụkarị',
        'faq.subtitle1': 'Ịchọrọ enyemaka? Anyị ',
        'faq.subtitle2': 'nwere azịza',
        'faq.0.question': 'Kedu ka m ga-esi malite?',
        'faq.0.answer':
            'Debanye aha maka akaụntụ, ị nwere ike ịmalite ikwurịta okwu na onye enyemaka AI anyị. Ị nwere ike ịjụ ajụjụ iwu, mepụta ikpe, ma nweta ihe eji enyemaka iwu dị iche iche.',
        'faq.1.question': 'Podus AI ọ na-anọchi anya Ndị Ọka Iwu?',
        'faq.1.answer':
            'Podus AI anaghị anọchi anya ndị ọka iwu na idozi ikpe iwu. Kama, ọ na-arụ ọrụ dị ka enyemaka mbụ maka nsogbu iwu.',
        'faq.2.question': "PodusAI ọ bụ n'efu?",
        'faq.2.answer':
            "Ee. PodusAI bụ n'efu kpamkpam. Ebumnuche anyị bụ ibelata amaghị iwu ma nyere ndị Nigeria ike site na nkwado iwu dị mfe.",
        'faq.3.question': 'PodusAI ọ bụ naanị maka ndị ọka iwu?',
        'faq.3.answer':
            'Mba. PodusAI na-ejere ma ndị ọka iwu ma ndị Nigeria ụbọchị niile ozi. Ma ịchọrọ ịchọpụta ụlọ ọrụ gọọmentị, ghọta ikike gị, ma ọ bụ nweta nduzi iwu mbụ, PodusAI bụ maka ndị obodo na-arụsi ọrụ ike.',
        'faq.4.question': 'Dị ka onye na-abụghị ọka iwu, gịnị ka PodusAI nwere ike inyere m aka?',
        'faq.4.answer':
            "PodusAI na-enyere gị aka ịkọ mmebi ikike mmadụ ma jikọọ na ndị ọka iwu n'efu; nweta nọmba PRO Ndị Uwe Ojii maka steeti 36 niile.",
        'faq.5.question': 'PodusAI ọ nwere ike ijikọ m na ndị ọka iwu?',
        'faq.5.answer':
            "Ee. Ọ bụrụ na ikpe gị metụtara mmebi ikike mmadụ ma ọ bụ mkpagbu dabere na okike, anyị ga-enyocha ya ma nye gị ọka iwu n'efu ma ọ dabara.",
    },

    // ─── HAUSA ───
    ha: {
        'nav.home': 'Gida',
        'nav.services': 'Ayyuka',
        'nav.help': 'Taimako',
        'nav.signUp': 'Yi Rajista',
        'nav.login': 'Shiga',
        'nav.signIn': 'Shiga',
        'nav.getStarted': 'Fara',

        'hero.socialProof': '31K+',
        'hero.socialProofText': 'Sun riga sun yi rajista',
        'hero.heading1': 'Manhajar Babba don ',
        'hero.heading2': "Duk Abin Da Ya Shafi Shari'a.",
        'hero.subtitle':
            "Podus ya haɗa taimakon shari'a na AI, sarrafa kwangiloli, haɗa lauyoyi, da tallafin 'yancin ɗan adam — duk a ƙarƙashin asusun Podus guda ɗaya mai tsaro.",
        'hero.ctaGetStarted': 'Fara',
        'hero.ctaHowItWorks': 'Duba Yadda Yake Aiki',

        'features.badge': "Mafi Kyawun Tsarin Shari'a",
        'features.headingGray': "Manhajar Shari'a Mai Sauƙaƙa ",
        'features.headingDark': "Adalci, Kasuwanci, da Rayuwar Shari'a Ta Yau Da Kullum",
        'features.headingMobileGray': "Manhajar Shari'a ta Babba don ",
        'features.headingMobileDark': 'Rayuwa da Kasuwanci',
        'features.subtitleDesktop': 'Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar.',
        'features.subtitleMobile':
            "Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar warware matsalolin shari'a — daga jagoranci nan take zuwa sarrafa kwangiloli da kare 'yancin ɗan adam.",
        'features.0.title': "Tallafin Shari'a Bai Kamata Ya Zama Gata Ba",
        'features.0.description':
            "Taimakon shari'a bai kamata ya dogara da kuɗin ka ba. Ko kana buƙatar amsoshi nan take, wakilci kyauta, ko tuntuɓar lauyoyi masu cancanta, Podus yana tabbatar da cewa ba za ka fuskanci matsalar shari'a kaɗai ba.",
        'features.1.title': 'Kariya Mai Wayo Don Kasuwancinka',
        'features.1.description':
            "Jumla guda ɗaya maras fahimta na iya kashe kasuwancinka kuɗin watanni uku. Podus Contract yana taimaka maka ka rubuta, duba, da sarrafa yarjejeniyoyi tare da binciken AI da bita daga masanin shari'a.",
        'features.2.title': 'Yi Hayar Da Amincewa',
        'features.2.description':
            "Podus manhajar AI ce. Podus Checker yana rage haɗarin hayar ma'aikata ta hanyar binciken asali kan aikin yi, rikodin laifi, kafofin watsa labarai, ilimi, da masu lamuni.",

        'services.heading': "Mafita Na Shari'a Masu Wayo Don Kowane Hali",
        'services.subtitle':
            "Bincika tsarin Podus — jerin ayyukan shari'a na AI da aka tsara don biyan kowane buƙata. Daga shawarar shari'a nan take zuwa sarrafa kwangiloli da samun damar lauyoyi, duk abin da kake buƙata don adalci da kasuwanci yana a wuri ɗaya.",
        'services.0.description':
            "Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar warware matsalolin shari'a — daga jagoranci nan take zuwa sarrafa kwangiloli da kare 'yancin ɗan adam.",
        'services.0.cta': "Gwada Mataimakin Shari'ar Mu",
        'services.1.description':
            "Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar warware matsalolin shari'a — daga jagoranci nan take zuwa sarrafa kwangiloli da kare 'yancin ɗan adam.",
        'services.1.cta': 'Yi Binciken Asali',
        'services.2.description':
            "Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar warware matsalolin shari'a — daga jagoranci nan take zuwa sarrafa kwangiloli da kare 'yancin ɗan adam.",
        'services.2.cta': 'Fara Kyauta',
        'services.3.description':
            "Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar warware matsalolin shari'a — daga jagoranci nan take zuwa sarrafa kwangiloli da kare 'yancin ɗan adam.",
        'services.3.cta': 'Shirya Alƙawari',
        'services.4.description':
            "Podus manhajar AI ce mai canza yadda mutane da kasuwanci ke samun damar warware matsalolin shari'a — daga jagoranci nan take zuwa sarrafa kwangiloli da kare 'yancin ɗan adam.",
        'services.4.cta': 'Nemi Taimako',

        'singleSignIn.tagline': 'Shiga guda ɗaya don duka.',
        'singleSignIn.heading': 'Shiga Guda Ɗaya',
        'singleSignIn.description':
            "Samu damar duk ayyukan Podus da asusu guda ɗaya mai tsaro. Babu buƙatar tuna kalmomin sirri da yawa — shiga sau ɗaya ka buɗe duk kayan aikin shari'a na AI.",
        'singleSignIn.cta': 'Fara',

        'podusFeatures.heading1': 'Podus AI ',
        'podusFeatures.heading2': 'Fasaloli',
        'podusFeatures.0.title': "Asusu Guda Ɗaya. Kowace Mafitar Shari'a.",
        'podusFeatures.0.description':
            "Samu damar duk ayyukan Podus da shiga guda ɗaya mai tsaro. Podus ya haɗa jagorar shari'a, tuntuɓar lauyoyi, warware rikici, kayan aikin kwangiloli, binciken asali, da taimakon shari'a a ƙarƙashin asusu guda ɗaya.",
        'podusFeatures.1.title': "Basirar Shari'a Ta AI",
        'podusFeatures.1.description':
            "Fahimta nan take kafin ka yi magana da lauya. Tare da Podus AI, masu amfani suna samun jagorar shari'a nan take, tallafin rubuta takardun, da binciken yanayi.",
        'podusFeatures.2.title': 'Haɗa Da Lauyoyi Na Gaske — A Kan Buƙata',
        'podusFeatures.2.description':
            'Daga shawarar AI zuwa ƙwarewar ɗan adam a cikin mintoci. Shirya alƙawarin bidiyo, nemi bitar kwangiloli, ko haɗa da lauyoyi masu ba da kyauta kai tsaye a cikin manhajar.',
        'podusFeatures.3.title': 'Kwangiloli Masu Wayo & Bin Diddigin Ayyuka',
        'podusFeatures.3.description':
            "Fahimci abin da ka sanya hannu — kuma kada ka rasa wa'adin. Podus Contract yana binciken yarjejeniyoyi, yana nuna haɗari, yana bayyana ayyuka, kuma yana haɗa ranakun muhimmanci zuwa kalandar ka.",
        'podusFeatures.4.title': 'Warware Rikici Da Ke Haɗe Da Kotu',
        'podusFeatures.4.description':
            'Shigar, bi diddigin, kuma warware rikici ta hanyar dijital. Ta hanyar Podus Multidoor, masu amfani za su iya miƙa ƙara kai tsaye zuwa Kotun Multi-Door don sasantawa, yanke hukunci, ko sulhu.',
        'podusFeatures.5.title': "Kayan Aikin Shari'a Masu Tsaro & Amintacce",
        'podusFeatures.5.description':
            "An gina don adalci. An tsara don kariya. Podus yana aiki tare da ƙa'idojin kare bayanai masu ƙarfi, sadarwa mai tsaro, sarrafa damar shiga bisa matsayi, da hanyoyin bincike cikakkun.",

        'glimpse.heading': "Kallo Na Tallafin Shari'a Marar Cikas, Wanda Podus Ke Ƙarfafawa",
        'glimpse.subtitle':
            "Podus ya haɗa duk buƙatun shari'ar ka a tsarin da ke haɗe. Da asusu guda ɗaya, za ka iya samun damar ayyuka da yawa — daga tallafin shari'a na AI zuwa haɗa lauyoyi da sarrafa kwangiloli.",
        'glimpse.stats': '33k',
        'glimpse.statsLabel': 'Masu amfani da suka riga suka shiga',
        'glimpse.howItWorks': 'Yadda yake aiki',

        'regions.heading': 'Tallafawa Masu Amfani A Fadin Nijeriya, Ghana, da Birtaniya',
        'regions.description':
            "Podus yana girma a yankuna da yawa, yana tabbatar da cewa mutane da kasuwanci za su iya samun tallafin shari'a mai aminci ko ina suke. Ayyukanmu suna dacewa da yanayin shari'ar kowace ƙasa.",
        'regions.tagline': "Abokin Shari'ar Ka A Fadin Yankuna.",

        'audience.heading': 'Wanene Zai Iya Amfani Da Podus',
        'audience.subtitle':
            'An tsara Podus don kowa — daga mutanen da ke neman adalci zuwa kasuwancin da ke sarrafa kwangiloli da lauyoyin da ke son sauƙaƙa aikinsu.',
        'audience.0.title': "Mutane & 'Yan Ƙasa",
        'audience.0.description':
            "Samu jagorar shari'a mai ma'ana, rubuta takardu, warware rikici, kuma haɗa da lauyoyi.",
        'audience.1.title': "Kasuwanci & 'Yan Kasuwa",
        'audience.1.description':
            "Rubuta kuma bincika kwangiloli, bi diddigin ayyuka, kuma gudanar da binciken asali na ma'aikata.",
        'audience.2.title': "Lauyoyi & Ɗaliban Shari'a & Ƙwararru",
        'audience.2.description':
            "Bayar da shawarwari, bitar kwangiloli, tallafa warware rikici, kuma shiga taimakon shari'a.",
        'audience.3.title': "Ƙungiyoyin Sa-kai & Ƙungiyoyin 'Yancin Ɗan Adam",
        'audience.3.description':
            "Tallafa al'umma, rubuta take haƙƙi, kuma haɗa mutanen da abin ya shafa da tallafin shari'a.",
        'audience.stat': '70%',
        'audience.statLabel': 'Lokacin Da Aka Tanada Kan\nAyyukan Yau Da Kullum',
        'audience.cta': 'Fara',

        'testimonials.heading': 'Shaidun',

        'faq.heading': 'Tambayoyin Da Ake Yawan Yi',
        'faq.subtitle1': 'Kana buƙatar taimako? Mun ',
        'faq.subtitle2': 'samu amsoshi',
        'faq.0.question': 'Yaya zan fara?',
        'faq.0.answer':
            "Ka yi rajista don asusu, za ka iya fara tattaunawa da mataimakin AI ɗin mu. Za ka iya yin tambayoyin shari'a, ƙirƙirar shari'u, da samun damar albarkatu daban-daban na shari'a.",
        'faq.1.question': 'Shin Podus AI yana maye gurbin Lauyoyi?',
        'faq.1.answer':
            "Podus AI baya maye gurbin matsayin lauyoyi wajen warware shari'u. Maimakon haka yana aiki a matsayin taimakon farko don matsalolin shari'a.",
        'faq.2.question': 'Shin PodusAI kyauta ne?',
        'faq.2.answer':
            "Eh. PodusAI kyauta ne gaba ɗaya. Manufar mu ita ce rage jahilcin shari'a da ƙarfafa 'yan Nijeriya da tallafin shari'a mai sauƙi.",
        'faq.3.question': 'Shin PodusAI don lauyoyi ne kawai?',
        'faq.3.answer':
            "A'a. PodusAI yana hidimar lauyoyi da 'yan Nijeriya na yau da kullum. Ko kana buƙatar gano hukumomin gwamnati, fahimtar haƙƙinka, ko samun jagorar shari'a na farko, PodusAI an gina shi don 'yan ƙasa masu himma.",
        'faq.4.question': 'A matsayin wanda ba lauya ba, menene PodusAI zai iya taimaka mini?',
        'faq.4.answer':
            "PodusAI yana taimaka maka ka ba da rahoton take haƙƙin ɗan adam kuma ka haɗa da lauyoyin kyauta; samun lambobin PRO na 'Yan Sanda don duk jihohi 36.",
        'faq.5.question': 'Shin PodusAI zai iya haɗa ni da lauyoyi?',
        'faq.5.answer':
            "Eh. Idan shari'ar ka ta shafi take haƙƙin ɗan adam ko cin zarafi dangane da jinsi, za mu kimanta ta mu kuma sanya maka lauya na kyauta inda ya dace.",
    },
};
